/**
 * Centralized help content library
 * Easy to update without touching components
 */

export interface HelpContent {
  title: string;
  content: string;
}

export const helpContent: Record<string, HelpContent> = {
  // Dashboard
  dashboardOverview: {
    title: 'Dashboard Overview',
    content: `The dashboard provides a comprehensive view of your reputation management performance.

Key Metrics:
• Total Sends: Number of emails/SMS sent through campaigns
• Open Rate: Percentage of messages that were opened
• Click Rate: Percentage of messages that received clicks
• Happy Rate: Percentage of positive ratings (4-5 stars)

These metrics help you track engagement and customer satisfaction over time.`,
  },

  dashboardKPITotalSends: {
    title: 'Total Sends',
    content: `This metric shows the total number of messages (emails and SMS) sent through all your campaigns.

To increase sends:
• Create more campaigns targeting different contact segments
• Import additional contacts to expand your reach
• Use sequences to send follow-up messages automatically`,
  },

  dashboardKPIOpenRate: {
    title: 'Open Rate',
    content: `Open rate measures the percentage of messages that recipients opened.

Industry benchmarks:
• Email: 20-25% is good, 25%+ is excellent
• SMS: 90%+ is typical (SMS is more immediate)

To improve open rates:
• Write compelling subject lines
• Send at optimal times for your audience
• Personalize messages with merge tags
• Segment contacts for relevant messaging`,
  },

  dashboardKPIClickRate: {
    title: 'Click Rate',
    content: `Click rate shows the percentage of messages that received clicks on links.

This indicates engagement and interest in your content.

To improve click rates:
• Use clear call-to-action buttons
• Make links prominent and easy to click
• Provide valuable content that encourages clicks
• Test different messaging approaches`,
  },

  dashboardKPIHappyRate: {
    title: 'Happy Rate',
    content: `Happy rate represents the percentage of customers who gave positive ratings (4-5 stars).

This is a key indicator of customer satisfaction.

To improve happy rate:
• Focus on delivering excellent customer experiences
• Follow up promptly with customers
• Address negative feedback quickly
• Use rating gates to capture feedback at the right moment`,
  },

  googleBusinessProfile: {
    title: 'Google Business Profile Integration',
    content: `Connect your Google Business Profile to display your current rating and review count on the dashboard.

Benefits:
• See your Google rating at a glance
• Track review count over time
• Monitor your online reputation
• Quick access to update your business information

Setup:
1. Click "Connect Now" to start the setup process
2. Search for your business by name or address
3. Verify ownership if required
4. Your rating will appear on the dashboard automatically

You can continue using the platform even if verification is pending.`,
  },

  // Campaigns
  campaignWorkflow: {
    title: 'Campaign Workflow',
    content: `Creating a campaign involves these steps:

1. Choose a Template: Select or create an email/SMS template with your message
2. Select a Sequence: Choose when and how often to send messages (optional)
3. Define Contacts: Choose who receives the campaign using filters or tags
4. Configure Settings: Set consent preferences and other options
5. Launch: Start the campaign and monitor performance

Best Practices:
• Test templates before sending to all contacts
• Use sequences for follow-up messages
• Respect consent preferences to maintain compliance
• Segment contacts for better engagement`,
  },

  campaignRespectConsent: {
    title: 'Respect Consent',
    content: `When enabled, this option ensures that only contacts who have given explicit consent will receive messages.

For SMS:
• Only contacts with sms_consent=true will receive SMS
• Required for TCPA compliance

For Email:
• Only contacts who have opted in will receive emails
• Required for CAN-SPAM compliance

Always enable this option to maintain legal compliance and protect your business.`,
  },

  campaignRespectSuppression: {
    title: 'Respect Suppression',
    content: `When enabled, contacts who have unsubscribed or been suppressed will not receive messages.

This includes:
• Contacts who clicked unsubscribe links
• Contacts who replied STOP (for SMS)
• Contacts manually added to suppression list
• Contacts with bounced email addresses

Always enable this to maintain good sender reputation and comply with regulations.`,
  },

  campaignFilters: {
    title: 'Campaign Filters',
    content: `Use filters to target specific groups of contacts:

• Tags: Include or exclude contacts with specific tags
• Custom Fields: Filter by any custom contact field
• Date Range: Target contacts created or updated in a date range

Examples:
• Send to "VIP" tagged contacts only
• Exclude contacts tagged "Do Not Contact"
• Target contacts from a specific region

Filters help you send the right message to the right audience.`,
  },

  // Feedback
  slaSystem: {
    title: 'SLA (Service Level Agreement) System',
    content: `The SLA system helps you track and meet response time commitments for customer feedback.

How it works:
• Each feedback item has a severity level (Critical, High, Medium, Low)
• Each severity has a target response time (hours)
• The system tracks time until response and warns of breaches

Severity Levels:
• Critical: 4 hours (urgent issues requiring immediate attention)
• High: 24 hours (important issues)
• Medium: 48 hours (standard issues)
• Low: 72 hours (minor issues)

Status Indicators:
• ON_TIME: Within target time
• WARNING: Approaching deadline (80% of SLA target)
• BREACHED: Past target time

Notifications:
• SLA Warnings: Sent to assigned users when feedback is at risk (80% of SLA target)
• SLA Breaches: Sent to assigned users and tenant owners when feedback exceeds SLA deadline`,
  },

  feedbackWorkflow: {
    title: 'Feedback Workflow',
    content: `Managing feedback follows this workflow:

1. NEW: Feedback is received and needs review
2. ASSIGNED: Feedback is assigned to a team member
3. IN_PROGRESS: Team member is actively working on it
4. RESOLVED: Issue has been addressed
5. CLOSED: Feedback is fully resolved and closed

Best Practices:
• Assign feedback quickly to meet SLA targets
• Add comments to track progress
• Update status as you work through issues
• Use tags to categorize feedback
• Resolve critical issues first`,
  },

  feedbackFiltering: {
    title: 'Filtering and Search',
    content: `Use filters and search to find specific feedback:

Filters:
• Status: Filter by feedback status (New, Assigned, etc.)
• Severity: Filter by severity level
• SLA Status: Find overdue or at-risk feedback
• Source: Filter by where feedback came from

Search:
• Search by contact name, email, or feedback text
• Use keywords to find related feedback
• Search is case-insensitive

Tips:
• Save common filter combinations
• Use search to find feedback from specific customers
• Filter by SLA status to prioritize urgent items`,
  },

  // Templates
  templateVariables: {
    title: 'Template Variables',
    content: `Use merge tags to personalize your messages:

Available Variables:
• {{first_name}} - Contact's first name
• {{last_name}} - Contact's last name
• {{email}} - Contact's email address
• {{company}} - Contact's company name
• {{rating_url}} - Link to rating page (for campaigns)

Example:
"Hi {{first_name}}, we'd love to hear about your experience with {{company}}!"

Best Practices:
• Always use {{first_name}} for personalization
• Test templates with sample data before sending
• Keep messages concise and clear
• Include clear call-to-action`,
  },

  templateBestPractices: {
    title: 'Template Best Practices',
    content: `Create effective templates with these tips:

1. Personalization: Use {{first_name}} and other variables
2. Clear Subject: Write compelling subject lines
3. Value First: Lead with value to the customer
4. Clear CTA: Make it obvious what you want them to do
5. Mobile Friendly: Keep messages concise for mobile
6. Test First: Always test templates before campaigns

Subject Line Tips:
• Keep it under 50 characters
• Create urgency or curiosity
• Personalize when possible
• Avoid spam trigger words

Message Tips:
• Get to the point quickly
• Use bullet points for clarity
• Include a clear call-to-action
• Make it easy to respond`,
  },

  // Rating Gate
  ratingGateFlow: {
    title: 'Rating Gate Settings',
    content: `Configure how your rating gate routes customer feedback based on their ratings.

Configuration Overview:
• Mode Selection: Choose Standard (includes Yelp) or Strict (Yelp-free, maximum compliance)
• Platform Integration: Connect Google Business, Facebook, and optionally Yelp
• Custom Messaging: Set policy-safe language and thank you messages
• Feedback Routing: Configure where low ratings (1-3 stars) are sent

How It Works:
1. Customers rate their experience (1-5 stars) via rating gate links in your campaigns
2. High ratings (4-5 stars): Automatically routed to public review platforms
3. Low ratings (1-3 stars): Captured privately for internal resolution

Key Settings:
• Google Business: Required for Google review redirects (syncs with dashboard)
• Facebook Page: Optional for Facebook review redirects
• Yelp Business: Only available in Standard mode (disabled in Strict mode)
• Custom Feedback Email: Where private feedback is sent (1-3 star ratings)

The rating gate helps you build a positive online reputation while addressing concerns privately. Configure these settings to control how feedback is routed.`,
  },

  ratingGatePolicyLanguage: {
    title: 'Policy-Safe Language',
    content: `This field contains the message shown to customers on the rating page.

Best Practices:
• Use neutral, helpful language
• Focus on helping other customers
• Avoid incentivizing reviews
• Be transparent about the process

Good Example:
"We value your feedback and would appreciate you sharing your experience to help other customers make informed decisions."

Avoid:
• "Leave us a 5-star review"
• "Get a discount for reviewing"
• Language that pressures customers

This helps maintain compliance with review platform policies.`,
  },

  ratingGateCustomFeedbackEmail: {
    title: 'Custom Feedback Email',
    content: `This email address is used for customers who submit low ratings (1-3 stars).

How It Works:
• When a customer rates 1-3 stars, they are shown a "Send Feedback via Email" button
• Clicking the button opens their email client with this address pre-filled
• The feedback is also automatically saved in your Feedback Inbox

Use Cases:
• Provide a direct way for customers to contact you about concerns
• Use a dedicated feedback email (e.g., feedback@yourbusiness.com)
• Route feedback to a specific team or department

Important Notes:
• This is optional - if not set, feedback is only saved in the Feedback Inbox
• Feedback entries are always created in your system regardless of this setting
• The email link gives customers an additional way to reach out directly

Best Practice:
Use a monitored email address that your team checks regularly, or integrate it with your support system.`,
  },

  // Settings
  complianceRequirements: {
    title: 'Compliance Requirements',
    content: `The platform helps you maintain compliance with key regulations:

TCPA (SMS):
• Requires explicit written consent before sending SMS
• Must provide opt-out instructions (Reply STOP)
• Must honor opt-outs immediately
• Maintain consent records

CAN-SPAM (Email):
• Must include unsubscribe link
• Must honor unsubscribe requests within 10 days
• Must identify message as advertisement if applicable
• Must include physical mailing address

GDPR/CCPA:
• Respect data subject access requests
• Provide privacy policy
• Allow data deletion requests
• Maintain data processing records

Always enable "Respect Consent" and "Respect Suppression" in campaigns.`,
  },

  apiKeys: {
    title: 'API Keys',
    content: `API keys allow you to integrate the platform with other applications.

Use Cases:
• Zapier integrations
• Custom webhooks
• Third-party tools
• Automated workflows

Security:
• Keep API keys secret
• Rotate keys regularly
• Revoke unused keys
• Never commit keys to version control

Creating a Key:
1. Click "Create API Key"
2. Give it a descriptive name
3. Copy the key immediately (you won't see it again)
4. Store it securely

Using the API:
• Include the key in Authorization header
• Format: "Bearer YOUR_API_KEY"
• See API documentation for endpoints`,
  },

  securityBestPractices: {
    title: 'Security Best Practices',
    content: `Protect your account with these practices:

Password:
• Use a strong, unique password
• Enable two-factor authentication if available
• Don't share passwords

API Keys:
• Rotate keys regularly
• Revoke unused keys
• Store keys securely

Team Access:
• Only invite trusted team members
• Use appropriate role permissions
• Remove access when team members leave

Data:
• Regularly backup important data
• Review access logs
• Monitor for suspicious activity

Compliance:
• Keep compliance attestations up to date
• Review privacy policies regularly
• Maintain consent records`,
  },

  // Contact Import
  contactImport: {
    title: 'Importing Contacts',
    content: `Import contacts from CSV files or connect via Zapier to sync from your CRM and other platforms.

CSV Import:
1. Prepare your CSV file with required columns: email, first_name, last_name
2. For SMS compliance, include: phone, sms_consent (true/false), consent_date (YYYY-MM-DD), consent_method
3. Click "Import Contacts" and select your CSV file
4. Review validation results and fix any errors
5. Import valid contacts

CSV Format:
• Required: email, first_name, last_name
• SMS fields: phone, sms_consent, consent_date, consent_method
• Optional: company, tags (comma-separated)

Zapier Integration:
Connect the platform to popular services for automatic contact syncing.`,
  },

  contactImportZapier: {
    title: 'Zapier Integration for Contact Import',
    content: `Connect the platform to your CRM and other services using Zapier for automatic contact syncing.

Popular Integrations:

CRM Systems:
• Salesforce - Sync leads and contacts automatically
• HubSpot - Keep contacts in sync between platforms
• Pipedrive - Import deals and contacts
• Zoho CRM - Two-way contact synchronization
• Monday.com - Sync contacts from boards

E-commerce Platforms:
• Shopify - Import customers automatically
• WooCommerce - Sync customer data
• BigCommerce - Connect your store customers
• Square - Import customer database

Email Marketing:
• Mailchimp - Sync subscribers
• Constant Contact - Import contact lists
• AWeber - Connect your email lists
• ConvertKit - Sync subscribers

Other Platforms:
• Google Sheets - Import from spreadsheets
• Airtable - Sync from databases
• Typeform - Import form submissions
• Gravity Forms - Sync form entries

Setting Up Zapier:
1. Go to Settings > API Keys
2. Create an API key for Zapier
3. In Zapier, search for "Reputation Manager"
4. Connect using your API key
5. Set up triggers (when contacts are created/updated in your source)
6. Configure actions (create/update contacts in the platform)

Benefits:
• Automatic syncing - no manual imports needed
• Real-time updates when contacts change
• Maintains data consistency across platforms
• Reduces manual data entry

Best Practices:
• Set up filters to only sync relevant contacts
• Map fields correctly between platforms
• Include consent information for SMS compliance
• Test your Zap before enabling it fully`,
  },

  // Team Management
  teamManagement: {
    title: 'Team Management',
    content: `Manage team members and their access to your account.

Roles:
• Owner: Full access including tenant management and deletion
• Admin: Can manage all features including team settings and billing
• User: Can view and manage campaigns, contacts, and feedback

Inviting Team Members:
1. Go to Team page
2. Click "Invite Team Member"
3. Enter email address and select role
4. Team member receives invitation email
5. They accept and join your organization

Managing Access:
• Change roles as needed
• Remove team members who no longer need access
• Assign feedback to specific team members
• Set permissions for different features

Best Practices:
• Use appropriate roles (don't give admin access unnecessarily)
• Regularly review team member access
• Remove access when team members leave
• Assign feedback based on expertise/availability`,
  },

  // Analytics
  analyticsDashboard: {
    title: 'Analytics Dashboard',
    content: `The Analytics Dashboard provides detailed insights into your reputation management performance.

Key Metrics Explained:

Engagement Metrics:
• Open Rate: Percentage of messages opened (indicates subject line effectiveness)
• Click Rate: Percentage of messages with clicks (shows content engagement)
• Response Rate: Percentage of customers who responded or rated

Feedback Metrics:
• Total Feedback: Number of feedback items received
• Average Rating: Mean rating across all feedback
• Happy Rate: Percentage of positive ratings (4-5 stars)
• Resolution Time: Average time to resolve feedback

Campaign Performance:
• Delivery Rate: Percentage of messages successfully delivered
• Bounce Rate: Percentage of failed deliveries
• Unsubscribe Rate: Percentage who opted out

Interpreting Charts:
• Line Charts: Show trends over time (e.g., open rates by day)
• Bar Charts: Compare metrics across categories (e.g., by campaign)
• Pie Charts: Show distribution (e.g., feedback by source)

Using Analytics:
• Identify best-performing campaigns
• Find optimal send times
• Track improvement over time
• Make data-driven decisions

Tips:
• Review analytics weekly
• Compare periods to see trends
• Focus on metrics that matter to your goals
• Use filters to analyze specific segments`,
  },

  // Sequences
  sequenceOverview: {
    title: 'What are Sequences?',
    content: `Sequences are multi-step message workflows that automatically send messages to contacts over time.

How Sequences Work:
1. Create a sequence with multiple steps (emails, SMS, delays)
2. Assign the sequence to a campaign
3. When a campaign runs, contacts receive messages according to the sequence timeline
4. Each step executes based on the delay settings from the previous step

Example Sequence:
• Step 1: Welcome email (immediate)
• Step 2: Delay 24 hours
• Step 3: Follow-up email (after delay)
• Step 4: Delay 3 days
• Step 5: Final reminder SMS (after delay)

Benefits:
• Automate follow-up messages
• Nurture leads over time
• Increase engagement with multiple touchpoints
• Save time with automated workflows

Best Practices:
• Start with 3-5 steps for optimal engagement
• Space messages appropriately (avoid overwhelming contacts)
• Use delays to give contacts time to respond
• Test sequences before using in production campaigns`,
  },

  sequenceSteps: {
    title: 'Sequence Steps',
    content: `Steps are the building blocks of your sequence. Each step can be:

Email Step:
• Sends an email using a selected template
• Template must be type "email" and status "active"
• Only active templates appear in the dropdown
• Tip: Set templates to "active" status before creating sequences

SMS Step:
• Sends an SMS using a selected template
• Template must be type "sms" and status "active"
• Only active templates appear in the dropdown
• Requires SMS consent from contacts

Delay Step:
• Waits before sending the next step
• Set delay in hours (0-23) and/or days (0+)
• Example: 1 day + 12 hours = 36 hours total
• Use delays to space out messages appropriately

Condition Step:
• Conditional logic (future feature)
• Currently not processed by backend
• Reserved for future workflow automation

Step Order:
• Steps execute in the order they appear
• Use the up/down arrows to reorder steps
• First step executes immediately when sequence starts
• Subsequent steps execute after their delay period

Important:
• Templates must be set to "active" status (not "draft") to appear in step editor
• Each email/SMS step requires a template
• At least one step is required to save a sequence`,
  },

  sequenceTemplates: {
    title: 'Using Templates in Sequences',
    content: `Templates are required for email and SMS steps in your sequence.

Template Requirements:
• Status: Must be "active" (not "draft" or "archived")
• Type: Must match step type (email template for email step, SMS template for SMS step)
• Only active templates appear in the template dropdown

Why Templates Must Be Active:
• Prevents using incomplete or untested templates
• Ensures only production-ready templates are used
• Maintains quality and consistency in your sequences

Setting Templates to Active:
1. Go to Templates page
2. Edit your template
3. Change status from "draft" to "active"
4. Save the template
5. Return to sequence editor - template will now appear

Template Best Practices:
• Test templates before setting to active
• Use variables ({{first_name}}, etc.) for personalization
• Keep messages concise and clear
• Include clear call-to-action
• Preview templates before using in sequences

Troubleshooting:
• Can't find a template? Check that it's set to "active" status
• Template not showing? Verify it matches the step type (email vs SMS)
• Still not visible? Refresh the page and try again`,
  },

  sequenceConfiguration: {
    title: 'Sequence Configuration',
    content: `Configure your sequence settings to control how it behaves:

Status:
• Draft: Sequence is being created/edited (not usable in campaigns)
• Active: Sequence is ready to use in campaigns
• Paused: Temporarily disabled (existing campaigns continue)
• Archived: Sequence is no longer used (historical only)

Max Duration (days):
• Optional limit on total sequence duration
• If set, sequence will not exceed this many days
• Helps ensure sequences complete within a timeframe
• Leave empty for no limit

Allow Opt-Out:
• Any step: Contacts can opt out at any point in the sequence
• First step only: Contacts can only opt out from the first message
• Never: Contacts cannot opt out (use carefully, ensure compliance)

Best Practices:
• Set status to "active" when sequence is ready
• Use max duration to prevent overly long sequences
• Choose opt-out settings based on your compliance needs
• Test sequences in draft mode before activating`,
  },

  sequenceTimeline: {
    title: 'Understanding Sequence Timeline',
    content: `The timeline shows when each step in your sequence will execute.

Timeline Calculation:
• Step 1: Executes immediately when sequence starts
• Step 2: Executes after Step 1's delay (if any)
• Step 3: Executes after Step 2's delay (if any)
• And so on...

Total Duration:
• Sum of all delays in the sequence
• Shown at the top of the timeline
• Example: 1 day + 2 days + 12 hours = 3 days, 12 hours

Delay Examples:
• Immediate: No delay (first step or step with no delay)
• +24 hours: Sends 1 day after previous step
• +3 days: Sends 3 days after previous step
• +1 day, 12 hours: Sends 1.5 days after previous step

Timeline Preview:
• Shows the order of steps visually
• Displays delay between each step
• Helps you understand the sequence flow
• Use to verify timing is appropriate

Best Practices:
• Space messages appropriately (don't overwhelm contacts)
• Use delays to give contacts time to respond
• Consider time zones when setting delays
• Test timeline before using in campaigns`,
  },

  sequenceValidation: {
    title: 'Sequence Validation',
    content: `Validate your sequence to check for errors before using it in campaigns.

What Validation Checks:
• Sequence has at least one step
• All steps have required fields (name, template for email/SMS)
• Email/SMS steps have valid templates selected
• Delay steps have at least one delay value
• Step orders are sequential (0, 1, 2, ...)
• No duplicate step orders

Validation Results:
• Valid: Sequence is ready to use
• Errors: Must be fixed before sequence can be used
• Warnings: Should be reviewed but don't block usage

Common Validation Errors:
• "At least one step is required" - Add steps to your sequence
• "Template is required for email/SMS steps" - Select a template
• "Template must be active" - Set template status to active
• "At least one delay value is required" - Add delay to delay step

Fixing Errors:
1. Review validation errors
2. Click on the step with the error
3. Fix the issue (e.g., select template, add delay)
4. Validate again to confirm fix

Best Practices:
• Validate before saving sequence
• Fix all errors before using in campaigns
• Review warnings even if sequence is valid
• Re-validate after making changes`,
  },
};

/**
 * Get help content by key
 */
export const getHelpContent = (key: string): HelpContent | undefined => {
  return helpContent[key];
};

/**
 * Get help content with fallback
 */
export const getHelpContentSafe = (key: string, fallback: HelpContent): HelpContent => {
  return helpContent[key] || fallback;
};

